"use client";

import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";

/* =========================================================
   ACCOUNT PAGE
   PRICING: FREE / STARTER ($9) / PRO ($39)
   ========================================================= */

export function Account() {
  /* -------------------- DATA -------------------- */
  const user = useQuery(api.users.getCurrentUser);
  const subscriptions = useQuery(api.users.getUserSubscriptions);

  const createUserProfile = useMutation(api.users.createUserProfile);
  const createCheckout = useAction(api.payments.createCheckout);
  const getCustomerPortal = useAction(api.payments.getCustomerPortal);
  const verifySubscription = useAction(api.payments.verifySubscriptionStatus);

  /* -------------------- STATE -------------------- */
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  /* -------------------- INIT PROFILE -------------------- */
  useEffect(() => {
    if (user && !user.profile) {
      createUserProfile();
    }
  }, [user, createUserProfile]);

  /* -------------------- LOADING -------------------- */
  if (!user) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-border" />
      </div>
    );
  }

  /* -------------------- CURRENT PLAN -------------------- */
  const currentRole = user.profile?.role || "free";

  /* -------------------- HANDLERS -------------------- */
  const handleUpgrade = async (planType: "starter" | "pro") => {
    setIsUpgrading(true);
    try {
      const res = await createCheckout({
        planType,
        returnUrl: window.location.origin + "/dashboard",
      });
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch {
      toast.error("Checkout failed");
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const res = await getCustomerPortal({ send_email: false });
      if (res.portal_url) {
        window.location.href = res.portal_url;
      }
    } catch {
      toast.error("Could not open billing portal");
    }
  };

  const handleRefresh = async () => {
    setIsVerifying(true);
    try {
      const res = await verifySubscription({});
      if (res.success) {
        toast.success("Subscription refreshed");
        window.location.reload();
      } else {
        toast.error(res.message);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  /* =========================================================
     RENDER
     ========================================================= */
  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
      {/* -------------------- HEADER -------------------- */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Account & Billing
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your plan and billing history
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={isVerifying}
          className="w-full md:w-auto rounded-md bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-black disabled:opacity-50"
        >
          {isVerifying ? "Refreshing..." : "Refresh Subscription"}
        </button>
      </div>

      {/* =========================================================
         PRICING CARDS
         ========================================================= */}
      <div className="mb-16 grid grid-cols-1 gap-6 md:grid-cols-3">

        {/* ================= FREE ================= */}
        <div
          className={
            currentRole === "free"
              ? "rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6 text-white shadow-xl"
              : "rounded-2xl border border-border bg-gray-50 p-6"
          }
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold">Free Plan</h3>
            <span className="rounded-full bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700">
              FREE
            </span>
          </div>

          <div className="mb-5">
            <span className="text-3xl font-bold">$0</span>
            <span className={`text-sm ${currentRole === "free" ? "text-gray-300" : "text-muted-foreground"}`}> /month</span>
          </div>

          {currentRole === "free" ? (
            <button
              disabled
              className="mb-6 w-full cursor-not-allowed rounded-md bg-gray-700 py-2 text-sm font-medium text-gray-300"
            >
              Current Plan
            </button>
          ) : (
            <button
              disabled
              className="mb-6 w-full cursor-not-allowed rounded-md bg-gray-200 py-2 text-sm font-medium text-muted-foreground"
            >
              Free Plan
            </button>
          )}

          <ul className={`space-y-2 text-sm ${currentRole === "free" ? "" : "text-muted-foreground"}`}>
            <li>✓ 3 video exports per month</li>
            <li>✓ Basic captions</li>
            <li>✓ Standard templates</li>
            <li>✓ 720p export quality</li>
            <li>✓ Watermark on exports</li>
          </ul>
        </div>

        {/* ================= STARTER ================= */}
        <div
          className={
            currentRole === "starter"
              ? "rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6 text-white shadow-xl"
              : "rounded-2xl border border-border bg-background p-6"
          }
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold">Starter Plan</h3>
            <span className="rounded-full bg-blue-500 px-3 py-1 text-xs font-medium text-white">
              STARTER
            </span>
          </div>

          <div className="mb-5">
            <span className="text-3xl font-bold">$9</span>
            <span className={`text-sm ${currentRole === "starter" ? "text-gray-300" : "text-muted-foreground"}`}> /month</span>
          </div>

          {currentRole === "starter" ? (
            <button
              disabled
              className="mb-6 w-full cursor-not-allowed rounded-md bg-gray-700 py-2 text-sm font-medium text-gray-300"
            >
              Current Plan
            </button>
          ) : currentRole === "pro" ? (
            <button
              disabled
              className="mb-6 w-full cursor-not-allowed rounded-md bg-gray-200 py-2 text-sm font-medium text-muted-foreground"
            >
              Included in Pro
            </button>
          ) : (
            <button
              onClick={() => handleUpgrade("starter")}
              disabled={isUpgrading}
              className="mb-6 w-full rounded-md bg-black py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              Upgrade Plan
            </button>
          )}

          <ul className={`space-y-2 text-sm ${currentRole === "starter" ? "" : ""}`}>
            <li>✓ 30 video exports per month</li>
            <li>✓ AI-powered captions</li>
            <li>✓ All templates</li>
            <li>✓ 1080p export quality</li>
            <li>✓ No watermark</li>
          </ul>
        </div>

        {/* ================= PRO ================= */}
        <div
          className={
            currentRole === "pro"
              ? "rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6 text-white shadow-xl"
              : "rounded-2xl border border-border bg-background p-6"
          }
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold">Pro Plan</h3>
            <span className="rounded-full bg-orange-500 px-3 py-1 text-xs font-medium text-white">
              PRO
            </span>
          </div>

          <div className="mb-5">
            <span className="text-3xl font-bold">$39</span>
            <span className={`text-sm ${currentRole === "pro" ? "text-gray-300" : "text-muted-foreground"}`}> /month</span>
          </div>

          {currentRole === "pro" ? (
            <button
              onClick={handleManageSubscription}
              className="mb-6 w-full rounded-md bg-gray-700 py-2 text-sm font-medium text-white hover:bg-gray-600"
            >
              Manage Subscription
            </button>
          ) : (
            <button
              onClick={() => handleUpgrade("pro")}
              disabled={isUpgrading}
              className="mb-6 w-full rounded-md bg-black py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              Upgrade Plan
            </button>
          )}

          <ul className={`space-y-2 text-sm ${currentRole === "pro" ? "" : ""}`}>
            <li>✓ Unlimited video exports</li>
            <li>✓ AI captions + background removal</li>
            <li>✓ Premium templates</li>
            <li>✓ 4K export quality</li>
            <li>✓ Priority support</li>
          </ul>
        </div>
      </div>

      {/* ================= BILLING HISTORY ================= */}
      <div className="rounded-xl border border-border bg-background p-6">
        <h2 className="mb-4 text-lg font-semibold">Billing History</h2>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b text-muted-foreground">
              <tr>
                <th className="py-2 text-left">Plan Name</th>
                <th className="text-left">Amount</th>
                <th className="text-left">Purchase Date</th>
                <th className="text-left">End Date</th>
                <th className="text-left">Status</th>
              </tr>
            </thead>

            <tbody>
              {subscriptions?.map((s) => (
                <tr key={s._id} className="border-b last:border-0">
                  <td className="py-3">{s.planType}</td>
                  <td>${s.price}</td>
                  <td>{new Date(s.startDate).toLocaleDateString()}</td>
                  <td>—</td>
                  <td className="font-medium text-green-600">
                    Success
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
